/**
 * Geo Region Selector component for selecting allowed regions by geographic area.
 *
 * @module components/ui/GeoRegionSelector
 */

import { Globe, MapPin } from 'lucide-react';
import { GEO_REGIONS, type GeoRegionKey } from './geoRegions';

interface GeoRegionSelectorProps {
  /** Currently selected geo regions */
  value: Set<GeoRegionKey>;
  /** Callback when selection changes */
  onChange: (selected: Set<GeoRegionKey>) => void;
  /** Helper text to display below the selector */
  helperText?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * A selector component for choosing geographic regions (Americas, Europe, APAC)
 */
export function GeoRegionSelector({
  value,
  onChange,
  helperText,
  disabled = false,
}: GeoRegionSelectorProps) {
  const handleToggle = (geoKey: GeoRegionKey) => {
    if (disabled) return;
    const newSelection = new Set(value);
    if (newSelection.has(geoKey)) {
      newSelection.delete(geoKey);
    } else {
      newSelection.add(geoKey);
    }
    onChange(newSelection);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Globe className="h-4 w-4" />
        <span>Allowed Regions</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(Object.entries(GEO_REGIONS) as [GeoRegionKey, (typeof GEO_REGIONS)[GeoRegionKey]][]).map(
          ([key, geo]) => (
            <label
              key={key}
              className={`relative flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                value.has(key)
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                  : 'border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="checkbox"
                checked={value.has(key)}
                onChange={() => handleToggle(key)}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{geo.name}</span>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{geo.description}</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {geo.regions.length} regions
                </p>
              </div>
            </label>
          )
        )}
      </div>

      {value.size === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          No regions selected — sandboxes can be created in any available region
        </p>
      )}

      {value.size > 0 && (
        <p className="text-sm text-primary-600 dark:text-primary-400">
          {value.size} region{value.size !== 1 ? 's' : ''} selected (
          {Array.from(value)
            .map((k) => GEO_REGIONS[k].name)
            .join(', ')}
          )
        </p>
      )}

      {helperText && <p className="text-xs text-gray-500 dark:text-gray-400">{helperText}</p>}
    </div>
  );
}
